import { computed, DestroyRef, effect, inject, Injectable, signal } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { environment } from '@callcenter-env/environment';
import { SHELL_V2_ENABLED, ShellRouteData } from './shell.config';

export type ThemeMode = 'light' | 'dark' | 'system';
export type Theme = 'light' | 'dark';

export const THEME_MODES: readonly ThemeMode[] = ['light', 'dark', 'system'];

/** The choice of the operator, kept on this computer (the canvas: «Выбор запоминается на этом компьютере»). `index.html` reads the same key before Angular starts. */
export const THEME_KEY = 'cc.theme';

/** The bundle of `angular.json` with the dark styles of ng-zorro (`inject: false`, ~630 kB): linked at the first dark page, on only while a new page is dark. */
export const NZ_DARK_HREF = 'nz-dark.css';
const NZ_DARK_LINK_ID = 'cc-nz-dark';

/**
 * The theme of the call-centre. Two areas follow it:
 * - the shell (the header, the menu and their windows) — always the operator's theme: they bind {@link theme} to `data-theme` of their hosts;
 * - the page — `data-theme` of `<html>` ({@link pageTheme}): the operator's theme on the new pages (route data `themed`), `light` on any other — the pages still on Metronic, the shared pages of the libraries —
 *   and the dark ng-zorro is on only while the page is dark.
 *
 * With the switch {@link SHELL_V2_ENABLED} off nothing is touched: the old shell, every page light.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  readonly enabled = inject(SHELL_V2_ENABLED);
  readonly mode = signal<ThemeMode>(readMode());
  readonly theme = computed<Theme>(() => resolve(this.mode(), this.systemDark()));
  readonly themedPage = signal(false);
  readonly pageTheme = computed<Theme>(() => this.themedPage() ? this.theme() : 'light');

  private readonly doc = inject(DOCUMENT);
  private readonly systemDark = signal(false);
  private link?: HTMLLinkElement;

  constructor() {
    if (!this.enabled) {
      // the old shell: every page light, whatever `index.html` read from a choice made on another build
      this.doc.documentElement.removeAttribute('data-theme');
      this.doc.documentElement.style.colorScheme = '';
      return;
    }
    const media = this.doc.defaultView?.matchMedia?.('(prefers-color-scheme: dark)');
    if (media) {
      this.systemDark.set(media.matches);
      const onChange = (e: MediaQueryListEvent) => this.systemDark.set(e.matches);
      media.addEventListener?.('change', onChange);
      inject(DestroyRef).onDestroy(() => media.removeEventListener?.('change', onChange));
    }
    const router = inject(Router, { optional: true });
    if (router) {
      // the page already open counts at once (a service made after the first navigation), every next one on its end — a navigation a guard cancelled changes nothing
      this.themedPage.set(isThemedPage(router.routerState.snapshot.root));
      const sub = router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
        .subscribe(() => this.themedPage.set(isThemedPage(router.routerState.snapshot.root)));
      inject(DestroyRef).onDestroy(() => sub.unsubscribe());
    }
    effect(() => this.apply(this.pageTheme()));
  }

  setMode(mode: ThemeMode): void {
    this.mode.set(mode);
    try {
      this.doc.defaultView?.localStorage.setItem(THEME_KEY, mode);
    } catch {
      // without localStorage the choice lasts until the page is reloaded
    }
  }

  private apply(theme: Theme): void {
    const html = this.doc.documentElement;
    html.setAttribute('data-theme', theme);
    html.style.colorScheme = theme;
    if (theme === 'dark' && !this.link) {
      // an operator who never opens a dark page never downloads it
      this.link = this.prepareDarkLink();
    }
    if (this.link) {
      // a page printed while dark keeps the light ng-zorro (and theme.css its light colours)
      this.link.media = theme === 'dark' ? 'screen' : 'not all';
    }
  }

  /**
   * The dark ng-zorro is linked ONCE, at the first dark page, right after the styles of the build (so the components' own styles keep the last word), and then switched by `media`: switched at once, its place
   * in the cascade never changes. The version in the address — the file of an `inject: false` bundle has no hash.
   */
  private prepareDarkLink(): HTMLLinkElement {
    const existing = this.doc.getElementById(NZ_DARK_LINK_ID) as HTMLLinkElement | null;
    if (existing) {
      return existing;
    }
    const el = this.doc.createElement('link');
    el.id = NZ_DARK_LINK_ID;
    el.rel = 'stylesheet';
    el.media = 'not all';
    el.href = `${NZ_DARK_HREF}?v=${encodeURIComponent(environment.version)}`;
    // by now the components have put their <style>s at the end of <head>: after the build's own `styles*.css`, not after whatever link came last
    const sheets = Array.from(this.doc.head.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]'));
    const after = sheets.find(l => /(^|\/)styles[^/]*\.css/.test(l.getAttribute('href') ?? '')) ?? sheets[sheets.length - 1] ?? null;
    if (after) {
      after.insertAdjacentElement('afterend', el);
    } else {
      this.doc.head.appendChild(el);
    }
    return el;
  }
}

function readMode(): ThemeMode {
  try {
    const v = localStorage.getItem(THEME_KEY) as ThemeMode | null;
    return v && THEME_MODES.includes(v) ? v : 'light';
  } catch {
    return 'light';
  }
}

function resolve(mode: ThemeMode, systemDark: boolean): Theme {
  return mode === 'system' ? (systemDark ? 'dark' : 'light') : mode;
}

/** The page follows the theme when a route of the active branch is marked `themed` (a parent marks its children); anything else is light. */
export function isThemedPage(root: ActivatedRouteSnapshot | null): boolean {
  for (let r: ActivatedRouteSnapshot | null = root; r; r = r.firstChild) {
    if ((r.data as ShellRouteData | undefined)?.themed) {
      return true;
    }
  }
  return false;
}
